import {
  TResult,
  makeSuccess,
  makeFailure,
  ISgApiService,
  jsonToBase64,
  base64ToJson,
  IEndpoint,
} from "@sg-core/index";

import { IReciever } from "../models/Reciever";
import {
  EmailAttachmentMapper,
  TEmailAttachments,
  TEmailAttachmentsDTO,
} from "../models/EmailAttachment";

/**
 * SEND EMAIL
 */

interface ISendEmailResultDTO {
  success: true;
  action: "laheta_sahkoposti";
}

export interface IEmailReciever extends IReciever {
  osoite?: string;
  nimi?: string;
  templateVariables?: { [key: string]: any };
}

interface ISendEmailRequestPayload {
  vastaanottaja?: { osoite: string; nimi: string };
  vastaanottajat?: Array<{ osoite: string; nimi: string }>;
  lahettaja?: { osoite?: string; nimi: string };
  sanomapohja: { tunniste: string; korvaukset?: { [key: string]: any } };
  liitetiedostot?: TEmailAttachmentsDTO;
}

export interface ISendEmailOptions {
  reciever: IEmailReciever;
  sender: { name: string; address?: string };
  templateKey: string;
  templateVariables?: { [key: string]: any };
  attachments?: TEmailAttachments;
}

export interface ISendMultipleEmailOptions {
  recievers: Array<IEmailReciever>;
  sender?: { name: string; address?: string };
  templateKey: string;
  templateVariables?: { [key: string]: any };
  attachments?: TEmailAttachments;
}

/**
 * SEND SMS
 */

export interface ISmsReciever extends IReciever {
  number: string;
  templateVariables?: { [key: string]: any };
}

interface ISendSmsRequestPayload {
  vastaanottaja?: { numero: string; nimi: string };
  vastaanottajat?: Array<{ numero: string; nimi: string }>;
  sanomapohja: { tunniste: string; korvaukset?: { [key: string]: any } };
}

export interface ISendMultipleSmsOptions {
  recievers: Array<ISmsReciever>;
  templateKey: string;
  templateVariables?: { [key: string]: any };
}

export interface ISendSmsOptions {
  reciever: ISmsReciever;
  templateKey: string;
  templateVariables?: { [key: string]: any };
}

/**
 * SAVE DATA PACKAGE
 */

interface ISaveDataPackageResultDTO {
  success: true;
  action: "tallenna_sanomaparametrit";
  id: string;
}

/**
 * GET DATA PACKAGE
 */

interface IGetDataPackageResultDTO {
  success: true;
  action: "hae_sanomaparametrit";
  base64data: string;
}

interface IGetAnonDataPackageResultDTO {
  success: true;
  action: "hae_sanomaparametrit_anonyymi";
  base64data: string;
  sid: string;
}

export interface ICommonService {
  sendEmail(options: ISendEmailOptions): Promise<TResult<any>>;
  sendSms(options: ISendSmsOptions): Promise<TResult<any>>;

  sendMultipleSms(options: ISendMultipleSmsOptions): Promise<TResult<any>>;
  sendMultipleEmail(options: ISendMultipleEmailOptions): Promise<TResult<any>>;

  saveDataPackage: (jsonData: {
    [key: string]: any;
  }) => Promise<TResult<{ id: string }>>;
  getDataPackage: (
    id: string
  ) => Promise<TResult<{ data: { [key: string]: any } }>>;

  getPackageWithReadonlySession: (
    id: string
  ) => Promise<TResult<{ data: { [key: string]: any }; token: string }>>;
  getApiTranslations: (
    keys: Array<string>,
    lang?: string
  ) => Promise<TResult<{ [key: string]: string }>>;
}

export const getCommonService = (
  apiService: ISgApiService,
  Enpoint: IEndpoint
): ICommonService => {
  /**
   * Send email from backend service using preconfigured templates
   */
  const sendEmail = async (
    options: ISendEmailOptions
  ): Promise<TResult<any>> => {
    const action = "laheta_sahkoposti";
    const body: ISendEmailRequestPayload = {
      vastaanottaja: {
        osoite: options.reciever.address || "",
        nimi: options.reciever.name,
      },
      lahettaja: { nimi: options.sender.name },
      sanomapohja: {
        tunniste: options.templateKey,
        korvaukset: options.templateVariables || {},
      },
      liitetiedostot: options.attachments?.length
        ? EmailAttachmentMapper.attachmentsToApi(options.attachments)
        : undefined,
    };

    const apiResponse = await apiService.sendAction<ISendEmailResultDTO>(
      Enpoint,
      action,
      body
    );

    if (apiResponse.isSuccess === true && apiResponse.value.success === true) {
      return makeSuccess({});
    }
    return apiResponse;
  };

  const sendMultipleEmail: ICommonService["sendMultipleEmail"] = async (
    options
  ) => {
    const action = "laheta_sahkoposti";
    const body: ISendEmailRequestPayload = {
      vastaanottajat: options.recievers.map((reciever) => ({
        osoite: reciever.address || "",
        nimi: reciever.name,
      })),
      lahettaja: options.sender ? { nimi: options.sender.name } : undefined,
      sanomapohja: {
        tunniste: options.templateKey,
        korvaukset: options.templateVariables || {},
      },
      liitetiedostot: options.attachments?.length
        ? EmailAttachmentMapper.attachmentsToApi(options.attachments)
        : undefined,
    };

    const apiResponse = await apiService.sendAction<ISendEmailResultDTO>(
      Enpoint,
      action,
      body
    );

    if (apiResponse.isSuccess === true && apiResponse.value.success === true) {
      return makeSuccess({});
    }
    return apiResponse;
  };

  /**
   * Send email from backend service using preconfigured templates
   */
  const sendSms = async (options: ISendSmsOptions): Promise<TResult<any>> => {
    const action = "laheta_tekstiviesti";
    const body: ISendSmsRequestPayload = {
      vastaanottaja: {
        numero: options.reciever.number,
        nimi: options.reciever.name,
      },
      sanomapohja: {
        tunniste: options.templateKey,
        korvaukset: options.templateVariables || {},
      },
    };
    const apiResponse = await apiService.sendAction<ISendEmailResultDTO>(
      Enpoint,
      action,
      body
    );
    if (apiResponse.isSuccess === true && apiResponse.value.success === true) {
      return makeSuccess({});
    }
    return apiResponse;
  };

  const sendMultipleSms: ICommonService["sendMultipleSms"] = async (
    options
  ) => {
    const action = "laheta_tekstiviesti";
    const body: ISendSmsRequestPayload = {
      vastaanottajat: options.recievers.map((reciever) => ({
        numero: reciever.number,
        nimi: reciever.name,
      })),
      sanomapohja: {
        tunniste: options.templateKey,
        korvaukset: options.templateVariables || {},
      },
    };
    const apiResponse = await apiService.sendAction<ISendEmailResultDTO>(
      Enpoint,
      action,
      body
    );

    if (apiResponse.isSuccess === true && apiResponse.value.success === true) {
      return makeSuccess({});
    }
    return apiResponse;
  };

  /**
   * Save unstructured JSON data to backend for later retrieval
   */
  const saveDataPackage = async (jsonData: {
    [key: string]: any;
  }): Promise<TResult<{ id: string }>> => {
    const action = "tallenna_sanomaparametrit";
    const body = {
      base64Data: jsonToBase64(jsonData),
    };

    const apiResponse = await apiService.sendAction<ISaveDataPackageResultDTO>(
      Enpoint,
      action,
      body
    );

    if (apiResponse.isSuccess === true) {
      return makeSuccess({ id: apiResponse.value.id });
    }
    return apiResponse;
  };

  const getDataPackage = async (
    id: string
  ): Promise<TResult<{ data: { [key: string]: any } }>> => {
    const action = "hae_sanomaparametrit";
    const body = { id };

    const apiResponse = await apiService.sendAction<IGetDataPackageResultDTO>(
      Enpoint,
      action,
      body
    );

    if (apiResponse.isSuccess === true) {
      try {
        const data = base64ToJson(apiResponse.value.base64data);
        return makeSuccess({ data });
      } catch (err) {
        return makeFailure(err as Error);
      }
    }
    return apiResponse;
  };

  const getPackageWithReadonlySession = async (
    id: string
  ): Promise<TResult<{ data: { [key: string]: any }; token: string }>> => {
    const action = "hae_sanomaparametrit_anonyymi";
    const body = { id };
    const apiResponse =
      await apiService.sendAction<IGetAnonDataPackageResultDTO>(
        Enpoint,
        action,
        body,
        true
      );

    if (apiResponse.isSuccess === true) {
      try {
        const data = base64ToJson(apiResponse.value.base64data);
        return makeSuccess({ data, token: apiResponse.value.sid });
      } catch (err) {
        return makeFailure(err as Error);
      }
    }
    return apiResponse;
  };

  const getApiTranslations: ICommonService["getApiTranslations"] = async (
    translations,
    lang
  ) => {
    const action = "hae_kaannostekstit_anonyymi";
    const body = {
      kieli: lang === "en" ? "us" : lang,
      tekstit: translations.reduce<Array<{ alkuperainen: string }>>(
        (acc, key) => [...acc, { alkuperainen: key }],
        []
      ),
    };
    const noToken = true;
    const apiResponse = await apiService.sendAction<{
      tekstit: Array<{ alkuperainen: string; kaannos: string }>;
    }>(Enpoint, action, body, noToken);

    if (apiResponse.isSuccess === true) {
      return makeSuccess(
        apiResponse.value.tekstit.reduce(
          (acc, item) => ({ ...acc, [item.alkuperainen]: item.kaannos }),
          {}
        )
      );
    }
    return apiResponse;
  };

  return {
    sendEmail,
    sendMultipleEmail,

    sendSms,
    sendMultipleSms,

    saveDataPackage,
    getDataPackage,
    getPackageWithReadonlySession,
    getApiTranslations,
  };
};
